import React, { useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../services/supabase';
import './AdminLogin.css';
import { AuthContext } from '../App';

const QuickAuth: React.FC = () => {
  const [full_name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [isSignIn, setIsSignIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useContext(AuthContext);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      let resolvedMode: 'signIn' | 'signUp' = isSignIn ? 'signIn' : 'signUp';
      // If signing in, ignore any other input fields (full_name/phone) and only use email/password
      if (isSignIn) {
        // Clear local registration-only fields to avoid accidental use
        // (they are not rendered in the form in sign-in mode, but be explicit)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _unusedFullName = '';
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const _unusedPhone = '';
      }

      let user: any = null;
      if (isSignIn) {
        // Explicit sign-in
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw signInError;
        user = signInData.user;
        resolvedMode = 'signIn';
      } else {
        // Try to sign up the user
        const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { full_name, phone } }
        });
        // If signUp returns an error, log it and switch to sign-in mode for 422 or already-registered messages
        if (signUpError) {
          console.debug('[QuickAuth] signUpError raw:', signUpError);
          const msg = String((signUpError as any)?.message || signUpError || '');
          const status = (signUpError as any)?.status || (signUpError as any)?.statusCode || null;
          const isAlreadyRegistered = status === 422 || msg.toLowerCase().includes('already') || msg.toLowerCase().includes('registered') || msg.toLowerCase().includes('exists');
          if (isAlreadyRegistered) {
            setIsSignIn(true);
            setError(null);
            console.info('[QuickAuth] Existing account detected, attempting password sign-in fallback');
            resolvedMode = 'signIn';
            const { data: signInData, error: fallbackSignInError } = await supabase.auth.signInWithPassword({ email, password });
            if (fallbackSignInError) {
              throw fallbackSignInError;
            }
            user = signInData.user;
          } else {
            throw signUpError;
          }
        } else {
          user = signUpData.user ?? null;
          // If signUp didn't return a user (for example email confirmation flows), try signIn as fallback
          if (!user) {
            const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({ email, password });
            if (signInError) throw signInError;
            user = signInData.user;
            resolvedMode = 'signIn';
          }
        }
      }
  if (!user) throw new Error('User not authenticated after sign up or login');

  // Ensure user exists in users table before creating booking
  // First try to retrieve booking from navigation state, fallback to sessionStorage
      let booking = location.state?.booking as any | undefined;
      if (!booking) {
        try {
          const raw = sessionStorage.getItem('pendingBooking');
          if (raw) booking = JSON.parse(raw);
        } catch (err) {
          console.warn('Failed to read pending booking from sessionStorage', err);
        }
      }
      if (!booking) throw new Error('Booking data missing');

      // If booking came from BookingPage it may use snake_case keys; normalize to the DB column names
      const normalizeBooking = (b: any) => ({
        service_id: b.service_id || b.serviceId || b.service || null,
        scheduled_date: b.scheduled_date || b.scheduledDate || null,
        scheduled_time: b.scheduled_time || b.scheduledTime || null,
        location_type: b.location_type || b.locationType || null,
        address: b.address ?? null,
        city: b.city ?? null,
        state: b.state ?? null,
        total_amount: b.total_amount ?? b.totalAmount ?? null,
        travel_fee: b.travel_fee ?? b.travelFee ?? 0,
        travel_miles: b.travel_miles ?? b.travelMiles ?? 0,
        notes: b.notes || null,
        guest_name: b.guest_name || b.guestName || null,
        guest_email: b.guest_email || b.guestEmail || null,
        guest_phone: b.guest_phone || b.guestPhone || null,
        service_price: b.service_price || b.price || null,
      });

      booking = normalizeBooking(booking);

      // helper to treat empty strings as missing
      const isEmpty = (v: any) => v == null || String(v).trim() === '';

      // Default location_type to 'dropoff' if not provided
      booking.location_type = booking.location_type || 'dropoff';

      // If location_type is dropoff, ensure address/city/state are set to business defaults if missing.
      const BUSINESS_ADDRESS = {
        address: '7230 Jemmah Lane',
        city: 'Jacksonville',
        state: 'Florida'
      };
      if (booking.location_type === 'dropoff') {
        if (isEmpty(booking.address)) booking.address = BUSINESS_ADDRESS.address;
        if (isEmpty(booking.city)) booking.city = BUSINESS_ADDRESS.city;
        if (isEmpty(booking.state)) booking.state = BUSINESS_ADDRESS.state;
      }

      // If service_id present, fetch service data to populate price/duration if missing
      let serviceData: any = null;
      // Fetch service info for enrichment when present
      if (booking.service_id) {
        const { data: sData, error: sErr } = await supabase
          .from('services')
          .select('*')
          .eq('id', booking.service_id)
          .single();
        if (sErr) {
          console.warn('Could not fetch service for booking enrichment:', sErr);
        } else {
          serviceData = sData;
          if (booking.total_amount == null) booking.total_amount = serviceData?.price ?? 0;
        }
      }

      // Defensive: ensure total_amount is present
      if (booking.total_amount == null) {
        throw new Error('Booking total_amount missing and could not be computed');
      }

      // Check for existing profile row and upsert if missing or coming from a new registration flow.
      const { data: existingProfile, error: existingProfileError } = await supabase
        .from('users')
        .select('id, full_name, email, phone')
        .eq('id', user.id)
        .maybeSingle();
      if (existingProfileError) {
        console.warn('[QuickAuth] Failed to fetch existing profile row:', existingProfileError);
      }

      const derivePreferredValue = (primary: any, ...fallbacks: any[]) => {
        const values = [primary, ...fallbacks];
        for (const value of values) {
          if (value != null && String(value).trim() !== '') return value;
        }
        return null;
      };

      const preferredName = derivePreferredValue(
        booking.guest_name,
        full_name,
        profile?.full_name,
        existingProfile?.full_name,
        user?.user_metadata?.full_name,
        user?.user_metadata?.name
      );
      const preferredEmail = derivePreferredValue(
        booking.guest_email,
        profile?.email,
        existingProfile?.email,
        email,
        user?.email
      );
      const preferredPhone = derivePreferredValue(
        booking.guest_phone,
        phone,
        profile?.phone,
        existingProfile?.phone,
        user?.phone
      );

      const shouldUpsertProfile = resolvedMode !== 'signIn' || !existingProfile;
      let profileValues = {
        full_name: existingProfile?.full_name || preferredName || null,
        email: existingProfile?.email || preferredEmail || null,
        phone: existingProfile?.phone || preferredPhone || null
      };

      if (shouldUpsertProfile) {
        const upsertPayload = {
          id: user.id,
          full_name: preferredName || existingProfile?.full_name || null,
          email: preferredEmail || existingProfile?.email || user?.email || null,
          phone: preferredPhone || existingProfile?.phone || null
        };
        const { error: upsertUserError } = await supabase
          .from('users')
          .upsert([upsertPayload], { onConflict: 'id' });
        if (upsertUserError) throw upsertUserError;
        profileValues = {
          full_name: upsertPayload.full_name,
          email: upsertPayload.email,
          phone: upsertPayload.phone
        };
      }

      // Build booking payload matching the structure used in BookingPage.
      // This ensures consistent column names and defaults so the DB receives the same data.
      const bookingPayload: any = {
        service_id: booking.service_id,
        // prefer booking guest fields, fallback to profile or auth form values
        guest_name: !isEmpty(booking.guest_name) ? booking.guest_name : (!isEmpty(profileValues.full_name) ? profileValues.full_name : null),
        guest_email: !isEmpty(booking.guest_email) ? booking.guest_email : (!isEmpty(profileValues.email) ? profileValues.email : null),
        guest_phone: !isEmpty(booking.guest_phone) ? booking.guest_phone : (!isEmpty(profileValues.phone) ? profileValues.phone : null),
        scheduled_date: booking.scheduled_date,
        scheduled_time: booking.scheduled_time,
        status: 'pending',
        // total_amount should already be computed (booking or serviceData)
        total_amount: booking.total_amount,
        payment_status: 'pending',
        stripe_payment_intent_id: null,
        notes: booking.notes || null,
        address: booking.address || null,
        city: booking.city || null,
        state: booking.state || null,
        location_type: booking.location_type || 'dropoff'
      };

      // include duration if serviceData provided
      if (serviceData && serviceData.duration) bookingPayload.duration = serviceData.duration;

      // If guest phone missing, prefer phone from auth profile
      if (isEmpty(bookingPayload.guest_phone) && !isEmpty(profile.phone)) {
        bookingPayload.guest_phone = profile.phone;
      }

      // Only include user_id when we have a valid authenticated user
      if (user && user.id) {
        bookingPayload.user_id = user.id;
      } else {
        // Defensive: ensure guest fields exist for unauthenticated bookings
        if (!bookingPayload.guest_name || !bookingPayload.guest_email || !bookingPayload.guest_phone) {
          throw new Error('Missing guest contact information for unauthenticated booking');
        }
      }

      const enrichedBooking = {
        ...bookingPayload,
        service_price: serviceData?.price ?? booking.service_price ?? bookingPayload.total_amount,
        travel_fee: booking.travel_fee || 0,
        travel_miles: booking.travel_miles || 0,
        add_ons: booking.add_ons || []
      };

      try {
        sessionStorage.setItem('pendingBooking', JSON.stringify(enrichedBooking));
      } catch (persistErr) {
        console.warn('Failed to persist pending booking after auth:', persistErr);
      }

      const meta = {
        service: serviceData,
        travelFee: booking.travel_fee || 0,
        travelMiles: booking.travel_miles || 0,
        addOns: booking.add_ons || []
      };

      navigate('/add-ons', { state: { booking: enrichedBooking, meta } });
    } catch (err: any) {
      setError(err.message || 'Authentication or booking failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="enhanced-auth-bg">
      <div className="enhanced-auth-card">
        <h2 className="enhanced-auth-title">Quick Authentication</h2>
        <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
          <button type="button" className={`mode-btn ${!isSignIn ? 'active' : ''}`} onClick={() => { setIsSignIn(false); setError(null); }} disabled={loading}>Register</button>
          <button type="button" className={`mode-btn ${isSignIn ? 'active' : ''}`} onClick={() => { setIsSignIn(true); setError(null); }} disabled={loading}>Sign in</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="enhanced-auth-group">
            {!isSignIn && (
              <input type="text" placeholder="Full Name" value={full_name} onChange={e=>setName(e.target.value)} required className="enhanced-auth-input" />
            )}
          </div>
          <div className="enhanced-auth-group">
            <input type="email" placeholder="Email" value={email} onChange={e=>setEmail(e.target.value)} required className="enhanced-auth-input" />
          </div>
          <div className="enhanced-auth-group">
            {!isSignIn && (
              <input type="tel" placeholder="Phone" value={phone} onChange={e=>setPhone(e.target.value)} required className="enhanced-auth-input" />
            )}
          </div>
          <div className="enhanced-auth-group">
            <input type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} required className="enhanced-auth-input" />
          </div>
          {error && <div className="enhanced-auth-error">{error}</div>}
          <button type="submit" className="enhanced-auth-btn" disabled={loading}>{loading ? 'Processing...' : 'Continue'}</button>
        </form>
      </div>
    </div>
  );
};

export default QuickAuth;
